// Phase of the app-wide "new meal plan" generation indicator. Derived (not stored)
// from the meal-plan request + result subscriptions — see PlanGenContext.
export enum PlanGenPhase {
  Idle = 'idle',
  Processing = 'processing',
  Ready = 'ready',
  Failed = 'failed'
}

export interface PlanGenContextValue {
  phase: PlanGenPhase
  // Seconds since the request started processing; only meaningful while Processing.
  elapsedSeconds: number
  // Name shown in the pill/popover/toast. Falls back to a generic label until the
  // result doc (which carries the real/derived name) lands.
  planName: string
  // The generated plan's id (request.planId) — target for the "Review" action.
  draftId: string | null
  // Number of dinners drafted in the ready plan (0 until the result is available).
  dinnerCount: number
  // Open the freshly generated draft for review (routes to /meal-plans/:draftId).
  review: () => void
  // Manual, free re-submit of the same request (preferences are already saved).
  retry: () => void
  // Hide a failed indicator for this request (client-local — the request doc is
  // backend-owned and terminal, so dismissal is tracked in localStorage).
  dismissFailed: () => void
  // Routing gap: no support destination wired yet — noop placeholder for now.
  getHelp: () => void
  // Progress-popover UI state, owned here (not in PlanPill) so it survives navigation.
  // PlanPill remounts on every route change (PageLayout is per-page), which would otherwise
  // reset this and re-fire the auto-open — see the auto-open effect in PlanGenContext.
  popoverOpen: boolean
  setPopoverOpen: (open: boolean) => void
  // Collapsed-to-dot state for the processing pill (the minimized affordance).
  popoverCollapsed: boolean
  setPopoverCollapsed: (collapsed: boolean) => void
  // Whether the "review" (Ready) pill should play its one-time bounce-in. Owned here in the
  // persistent provider so the pop fires once when a plan lands and then stays still across
  // navigations (PlanPill remounts per route). False once the current draft has popped;
  // resets automatically when a new plan (new draftId) supersedes.
  shouldPopReadyPill: boolean
  // Called by the Ready pill after it has popped, so subsequent remounts don't re-animate.
  markReadyPillPopped: (draftId: string) => void
}
